import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, startOfDay, startOfMonth, startOfYear, subDays, isSameDay } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

interface DateRangePresetPickerProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onDateRangeChange: (from: Date | undefined, to: Date | undefined) => void;
  className?: string;
}

type PresetKey = 
  | "today" 
  | "last7days" 
  | "lastMonth" 
  | "last3months" 
  | "last6months" 
  | "last12months" 
  | "mtd" 
  | "qtd" 
  | "ytd" 
  | "always";

interface DatePreset {
  key: PresetKey;
  label: string;
}

const DATE_PRESETS: DatePreset[] = [
  { key: "today", label: "Hoy" },
  { key: "last7days", label: "Últimos 7 días" },
  { key: "lastMonth", label: "Último mes" },
  { key: "last3months", label: "Últimos 3 meses" },
  { key: "last6months", label: "Últimos 6 meses" },
  { key: "last12months", label: "Últimos 12 meses" },
  { key: "mtd", label: "Acumulado del mes" },
  { key: "qtd", label: "Acumulado del trimestre" },
  { key: "ytd", label: "Acumulado del año" },
  { key: "always", label: "Siempre" },
];

const WEEKDAYS = ["LU", "MA", "MI", "JU", "VI", "SA", "DO"];

function getQuarterStart(date: Date): Date {
  const month = date.getMonth();
  const year = date.getFullYear();
  
  if (month <= 2) return new Date(year, 0, 1);
  if (month <= 5) return new Date(year, 3, 1);
  if (month <= 8) return new Date(year, 6, 1);
  return new Date(year, 9, 1);
}

function getPresetDateRange(presetKey: PresetKey): { from: Date | undefined; to: Date | undefined } {
  const today = startOfDay(new Date());
  
  switch (presetKey) {
    case "today":
      return { from: today, to: today };
    case "last7days":
      return { from: subDays(today, 6), to: today };
    case "lastMonth":
      return { from: subDays(today, 29), to: today };
    case "last3months":
      return { from: subDays(today, 89), to: today };
    case "last6months":
      return { from: subDays(today, 179), to: today };
    case "last12months":
      return { from: subDays(today, 364), to: today };
    case "mtd":
      return { from: startOfMonth(today), to: today };
    case "qtd":
      return { from: getQuarterStart(today), to: today };
    case "ytd":
      return { from: startOfYear(today), to: today };
    case "always":
    default:
      return { from: undefined, to: undefined };
  }
}

function detectPresetFromDates(from: Date | undefined, to: Date | undefined): PresetKey | null {
  if (!from && !to) return "always";
  if (!from || !to) return null;
  
  for (const preset of DATE_PRESETS) {
    const { from: presetFrom, to: presetTo } = getPresetDateRange(preset.key);
    if (presetFrom && presetTo && isSameDay(from, presetFrom) && isSameDay(to, presetTo)) {
      return preset.key;
    }
  }
  return null;
}

function formatDateShort(date: Date): string {
  return format(date, "d 'de' MMM yy", { locale: es });
}

function formatDateInput(date: Date | undefined): string {
  if (!date) return "DD/MM/AA";
  return format(date, "dd/MM/yy");
}

// Generate calendar days for a month organized by weeks
function getCalendarDays(year: number, month: number): (Date | null)[][] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  
  // Adjust for Monday start (0 = Monday, 6 = Sunday)
  let startDayOfWeek = firstDay.getDay() - 1;
  if (startDayOfWeek < 0) startDayOfWeek = 6;
  
  const weeks: (Date | null)[][] = [];
  let currentWeek: (Date | null)[] = [];
  
  // Fill empty days at start
  for (let i = 0; i < startDayOfWeek; i++) {
    currentWeek.push(null);
  }
  
  // Fill days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(new Date(year, month, day));
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  
  // Complete last week if needed
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    weeks.push(currentWeek);
  }
  
  return weeks;
}

interface CalendarMonthProps {
  year: number;
  month: number;
  selectedFrom: Date | undefined;
  selectedTo: Date | undefined;
  onDayClick: (date: Date) => void;
  isSelectingRange: boolean;
}

const CalendarMonth = ({ 
  year, 
  month, 
  selectedFrom, 
  selectedTo, 
  onDayClick,
  isSelectingRange 
}: CalendarMonthProps) => {
  const weeks = useMemo(() => getCalendarDays(year, month), [year, month]);
  const today = startOfDay(new Date());
  
  const isSelected = (date: Date) => {
    if (selectedFrom && isSameDay(date, selectedFrom)) return true;
    if (selectedTo && isSameDay(date, selectedTo)) return true;
    return false;
  };
  
  const isInRange = (date: Date) => {
    if (!selectedFrom || !selectedTo) return false;
    return date > selectedFrom && date < selectedTo;
  };
  
  const isRangeStart = (date: Date) => selectedFrom && isSameDay(date, selectedFrom);
  const isRangeEnd = (date: Date) => selectedTo && isSameDay(date, selectedTo);
  const isToday = (date: Date) => isSameDay(date, today);
  
  return (
    <div className="w-[252px]">
      {/* Month title */}
      <div className="text-center text-sm font-medium mb-3 capitalize">
        {format(new Date(year, month), "MMMM yyyy", { locale: es })}
      </div>
      
      {/* Weekday headers - CSS Grid 7 columns */}
      <div className="grid grid-cols-7">
        {WEEKDAYS.map(day => (
          <div 
            key={day} 
            className="h-9 w-9 flex items-center justify-center text-xs text-muted-foreground font-medium"
          >
            {day}
          </div>
        ))}
      </div>
      
      {/* Days grid - CSS Grid 7 columns */}
      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} className="grid grid-cols-7">
          {week.map((date, dayIdx) => (
            <div 
              key={dayIdx} 
              className={cn(
                "h-9 w-9 flex items-center justify-center relative",
                // Range background
                date && isInRange(date) && "bg-primary/10",
                // Range start - half background on right
                date && isRangeStart(date) && selectedTo && !isSameDay(selectedFrom!, selectedTo) && "bg-gradient-to-r from-transparent to-primary/10",
                // Range end - half background on left
                date && isRangeEnd(date) && selectedFrom && !isSameDay(selectedFrom, selectedTo!) && "bg-gradient-to-l from-transparent to-primary/10"
              )}
            >
              {date && (
                <button
                  type="button"
                  onClick={() => onDayClick(date)}
                  className={cn(
                    "h-8 w-8 rounded-full text-sm font-normal transition-colors",
                    "hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20",
                    // Selected state (start or end)
                    isSelected(date) && "bg-primary text-primary-foreground hover:bg-primary",
                    // Today indicator
                    isToday(date) && !isSelected(date) && "ring-1 ring-primary text-primary",
                    // Default text
                    !isSelected(date) && !isToday(date) && "text-foreground"
                  )}
                >
                  {date.getDate()}
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const DateRangePresetPicker = ({
  dateFrom,
  dateTo,
  onDateRangeChange,
  className,
}: DateRangePresetPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFrom, setTempFrom] = useState<Date | undefined>(dateFrom);
  const [tempTo, setTempTo] = useState<Date | undefined>(dateTo);
  const [selectedPreset, setSelectedPreset] = useState<PresetKey | null>(() => 
    detectPresetFromDates(dateFrom, dateTo)
  );
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  
  // Display month state (left calendar)
  const [displayMonth, setDisplayMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  // Calculate right calendar month (next month)
  const rightMonth = useMemo(() => {
    if (displayMonth.month === 11) {
      return { year: displayMonth.year + 1, month: 0 };
    }
    return { year: displayMonth.year, month: displayMonth.month + 1 };
  }, [displayMonth]);

  const goToPreviousMonth = useCallback(() => {
    setDisplayMonth(prev => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setDisplayMonth(prev => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  }, []);

  // Sync with external props when popover opens
  useEffect(() => {
    if (isOpen) {
      setTempFrom(dateFrom);
      setTempTo(dateTo);
      setSelectedPreset(detectPresetFromDates(dateFrom, dateTo));
      setIsSelectingRange(false);
      
      // Set display month based on selected date or current month
      if (dateFrom) {
        setDisplayMonth({ year: dateFrom.getFullYear(), month: dateFrom.getMonth() });
      } else {
        const today = new Date();
        setDisplayMonth({ year: today.getFullYear(), month: today.getMonth() });
      }
    }
  }, [isOpen, dateFrom, dateTo]);

  // Generate trigger button text
  const triggerText = useMemo(() => {
    const detectedPreset = detectPresetFromDates(dateFrom, dateTo);
    
    if (detectedPreset) {
      const preset = DATE_PRESETS.find(p => p.key === detectedPreset);
      return `Fecha: ${preset?.label || "Siempre"}`;
    }
    
    if (dateFrom && dateTo) {
      if (isSameDay(dateFrom, dateTo)) {
        return `Fecha: ${formatDateShort(dateFrom)}`;
      }
      return `Fecha: ${formatDateShort(dateFrom)} → ${formatDateShort(dateTo)}`;
    }
    
    if (dateFrom) {
      return `Fecha: ${formatDateShort(dateFrom)}`;
    }
    
    return "Fecha: Siempre";
  }, [dateFrom, dateTo]);

  const handlePresetClick = useCallback((presetKey: PresetKey) => {
    const { from, to } = getPresetDateRange(presetKey);
    setTempFrom(from);
    setTempTo(to);
    setSelectedPreset(presetKey);
    setIsSelectingRange(false);
  }, []);

  const handleDayClick = useCallback((date: Date) => {
    setSelectedPreset(null);
    
    if (!tempFrom || (tempFrom && tempTo)) {
      // Start new selection
      setTempFrom(date);
      setTempTo(undefined);
      setIsSelectingRange(true);
    } else {
      // Complete range
      if (date < tempFrom) {
        setTempTo(tempFrom);
        setTempFrom(date);
      } else if (isSameDay(date, tempFrom)) {
        // Same day selected - set both to same date
        setTempTo(date);
      } else {
        setTempTo(date);
      }
      setIsSelectingRange(false);
    }
  }, [tempFrom, tempTo]);

  const handleApply = useCallback(() => {
    const finalTo = tempTo || tempFrom;
    onDateRangeChange(tempFrom, finalTo);
    setIsOpen(false);
  }, [tempFrom, tempTo, onDateRangeChange]);

  const handleClear = useCallback(() => {
    setTempFrom(undefined);
    setTempTo(undefined);
    setSelectedPreset("always");
    setIsSelectingRange(false);
  }, []);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-10 px-4 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 font-normal justify-start gap-2",
            className
          )}
        >
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-foreground">{triggerText}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-auto p-0 pointer-events-auto" 
        align="end"
        sideOffset={8}
      >
        <div className="flex">
          {/* Left panel: Presets */}
          <div className="w-48 border-r border-border py-2">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                onClick={() => handlePresetClick(preset.key)}
                className={cn(
                  "w-full text-left px-4 py-2 text-sm transition-colors",
                  selectedPreset === preset.key
                    ? "bg-primary/10 text-primary border-l-2 border-primary font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>
          
          {/* Right panel: Custom Calendar */}
          <div className="p-4">
            {/* Date inputs */}
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Inicio</label>
                <div className="h-9 px-3 rounded-md border border-input bg-background flex items-center text-sm">
                  {formatDateInput(tempFrom)}
                </div>
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Final</label>
                <div className="h-9 px-3 rounded-md border border-input bg-background flex items-center text-sm">
                  {isSelectingRange && !tempTo ? "DD/MM/AA" : formatDateInput(tempTo)}
                </div>
              </div>
            </div>
            
            {/* Two-month calendar with navigation */}
            <div className="flex items-start gap-2">
              {/* Left navigation arrow */}
              <button
                type="button"
                onClick={goToPreviousMonth}
                className="mt-0.5 h-7 w-7 flex items-center justify-center rounded-md border border-input hover:bg-muted transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              {/* First month */}
              <CalendarMonth
                year={displayMonth.year}
                month={displayMonth.month}
                selectedFrom={tempFrom}
                selectedTo={tempTo}
                onDayClick={handleDayClick}
                isSelectingRange={isSelectingRange}
              />
              
              {/* Second month */}
              <CalendarMonth
                year={rightMonth.year}
                month={rightMonth.month}
                selectedFrom={tempFrom}
                selectedTo={tempTo}
                onDayClick={handleDayClick}
                isSelectingRange={isSelectingRange}
              />
              
              {/* Right navigation arrow */}
              <button
                type="button"
                onClick={goToNextMonth}
                className="mt-0.5 h-7 w-7 flex items-center justify-center rounded-md border border-input hover:bg-muted transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="text-muted-foreground"
              >
                Borra
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Aplica
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePresetPicker;
